// Enhanced API client with CSRF protection and token refresh
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://family-membership-system.jositett.workers.dev';
const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class ApiClient {
  private csrfToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private lastAuthCheck: number = 0;
  private readonly AUTH_CHECK_INTERVAL = 30000; // 30 seconds between auth checks

  private async getCsrfToken(): Promise<string | null> {
    if (this.csrfToken) return this.csrfToken;

    try {
      const response = await fetch(`${API_BASE_URL}/api/csrf-token`);
      if (response.ok) {
        const data = await response.json() as any;
        this.csrfToken = data.data?.csrfToken || response.headers.get('X-CSRF-Token');
        return this.csrfToken;
      }
    } catch (error) {
      console.warn('Failed to get CSRF token:', error);
    }
    return null;
  }

  private async getAuthHeaders(includeCSRF = false): Promise<HeadersInit> {
    // Try both token storage methods for backward compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };

    if (includeCSRF) {
      const csrfToken = await this.getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return headers;
  }

  private async refreshToken(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json() as any;
        if (data.success && data.data.token) {
          localStorage.setItem('auth_token', data.data.token);
          return true;
        }
      }
    } catch (error) {
      console.warn('Token refresh failed:', error);
    }

    // Clear invalid tokens
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    return false;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    if (USE_MOCK_API) {
      return this.mockRequest<T>(endpoint, options);
    }

    const method = options.method || 'GET';
    const needsCSRF = !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
    const timeoutMs = endpoint.startsWith('/api/auth/login') ? 15000 : 30000;

    const fetchWithTimeout = async (url: string, fetchOptions: RequestInit) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(url, {
          ...fetchOptions,
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      const headers = await this.getAuthHeaders(needsCSRF);

      const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      // Handle 401 Unauthorized - try token refresh with rate limiting
      if (response.status === 401 && localStorage.getItem('refresh_token')) {
        const now = Date.now();
        if (now - this.lastAuthCheck < this.AUTH_CHECK_INTERVAL) {
          console.warn('Rate limiting auth refresh - skipping refresh attempt');
          return { success: false, error: 'Authentication required' };
        }
        this.lastAuthCheck = now;
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry request with new token
          const newHeaders = await this.getAuthHeaders(needsCSRF);
          const retryResponse = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
              ...newHeaders,
              ...options.headers
            }
          });

          if (retryResponse.ok) {
            const data = await retryResponse.json() as ApiResponse<T>;
            return data;
          }
        }

        // For admin member views, try to restore original token before clearing everything
        const originalToken = localStorage.getItem('original_auth_token');
        if (originalToken && localStorage.getItem('user_data')) {
          try {
            const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
            if (userData.isAdminMemberView || userData.isDevMemberView) {
              // Restore original admin token
              localStorage.setItem('auth_token', originalToken);
              localStorage.removeItem('original_auth_token');

              // Retry with original token
              const originalHeaders = await this.getAuthHeaders(needsCSRF);
              const originalResponse = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
                ...options,
                headers: {
                  ...originalHeaders,
                  ...options.headers
                }
              });

              if (originalResponse.ok) {
                const data = await originalResponse.json() as ApiResponse<T>;
                return data;
              }
            }
          } catch (parseError) {
            console.warn('Failed to parse user data for token restoration:', parseError);
          }
        }

        // Clear invalid tokens and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('original_auth_token');

        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return { success: false, error: 'Authentication required' };
      }

      // Handle CSRF token errors
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({})) as any;
        if (errorData.error?.includes('CSRF')) {
          // Clear CSRF token and retry
          this.csrfToken = null;
          const newHeaders = await this.getAuthHeaders(needsCSRF);
          const retryResponse = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
              ...newHeaders,
              ...options.headers
            }
          });

          if (retryResponse.ok) {
            const data = await retryResponse.json() as ApiResponse<T>;
            return data;
          }
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as any;
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}`
        };
      }

      const data = await response.json() as ApiResponse<T>;
      return data;
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please try again.' };
      }
      console.warn('API request failed, falling back to mock:', error);
      return this.mockRequest<T>(endpoint, options);
    }
  }

  private async mockRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    // Simple mock responses for development
    await new Promise(resolve => setTimeout(resolve, 500));

    if (endpoint === '/api/auth/login') {
      const body = JSON.parse(options.body as string);

      // Admin login
      if (body.username === 'admin' && body.password === 'admin123') {
        const user = {
          id: 'admin_1',
          username: 'admin',
          role: 'admin',
          permissions: ['MANAGE_USERS', 'VIEW_MEMBERS', 'APPROVE_FINANCES'],
          townId: null
        };
        const token = 'mock_admin_token';
        const refreshToken = 'mock_refresh_token';
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        return { success: true, data: { user, token, refreshToken } as any as T };
      }

      // Member login
      if (body.username === 'testmember' && body.password === 'member123' && body.role === 'member') {
        const user = {
          id: 'member_test_member_1',
          username: 'testmember',
          role: 'member',
          permissions: ['VIEW_MEMBERS', 'VIEW_FINANCES', 'VIEW_BENEFITS'],
          townId: '1',
          memberId: 'test_member_1'
        };
        const token = 'mock_member_token';
        const refreshToken = 'mock_member_refresh_token';
        localStorage.setItem('auth_token', token);
        localStorage.setItem('refresh_token', refreshToken);
        return { success: true, data: { user, token, refreshToken } as any as T };
      }

      return { success: false, error: 'Invalid credentials' };
    }

    if (endpoint.startsWith('/api/')) {
      return { success: true, data: [] as T };
    }

    return { success: false, error: 'Endpoint not found' };
  }
}

const client = new ApiClient();

export const apiClient = {
  request: <T>(endpoint: string, options?: RequestInit) => client.request<T>(endpoint, options),
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined | null>) => {
    let url = endpoint;
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }
    return client.request<T>(url, { method: 'GET' });
  },
  post: <T>(endpoint: string, data?: unknown) => client.request<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  }),
  put: <T>(endpoint: string, data?: unknown) => client.request<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  }),
  delete: <T>(endpoint: string) => client.request<T>(endpoint, { method: 'DELETE' })
};
